import React, { useState, useEffect, useCallback, useMemo, useReducer } from 'react';
import { useAuth } from '../../hooks/useAuth';
import EnhancedInterviewScheduleModal from '../../components/interview/EnhancedInterviewScheduleModal';
import InterviewDetailsModal from '../../components/interviews/InterviewDetailsModal';
import InterviewRescheduleModal from '../../components/interviews/InterviewRescheduleModal';
import InterviewCancelModal from '../../components/interviews/InterviewCancelModal';
import LazyInterviewList from '../../components/interview/LazyInterviewList';
import VAHeaderNav from '../../components/navigation/VAHeaderNav';
import Button from '../../components/ui/Button';
import supabaseService from '../../services/supabaseService';
import { supabase } from '../../utils/supabase';
import { Video, Phone, Building2, Briefcase, Calendar, CheckCircle, BarChart3, Star } from 'lucide-react';

// ========== Constants ==========
const ITEMS_PER_PAGE = 12;

// ========== Pure Utility Functions (Outside Component) ==========
const INTERVIEW_STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  default: 'bg-gray-100 text-gray-800'
};

const INTERVIEW_TYPE_ICONS = {
  video: Video,
  phone: Phone,
  'in-person': Building2,
  default: Briefcase
};

const getStatusColor = (status) => INTERVIEW_STATUS_COLORS[status] || INTERVIEW_STATUS_COLORS.default;

const getTypeIcon = (type) => INTERVIEW_TYPE_ICONS[type] || INTERVIEW_TYPE_ICONS.default;

const formatDateTime = (date) => {
  const now = new Date();
  const diffInHours = Math.abs(date - now) / (1000 * 60 * 60);
  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  
  if (diffInHours < 24) {
    return `Today at ${timeStr}`;
  } else if (diffInHours < 48) {
    return date > now ? `Tomorrow at ${timeStr}` : `Yesterday at ${timeStr}`;
  }
  
  return date.toLocaleDateString('en-US', { 
    weekday: 'short',
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const parseFeedback = (feedback) => {
  if (!feedback) return '';
  if (typeof feedback === 'string') {
    try {
      const parsed = JSON.parse(feedback);
      return parsed.feedback || '';
    } catch {
      return feedback;
    }
  }
  if (typeof feedback === 'object' && feedback !== null) {
    return feedback.feedback || '';
  }
  return String(feedback);
};

const formatInterviewData = (interview, currentUser) => {
  // Log raw interview data for debugging
  if (!interview.interviewer_id || !interview.candidate_id) {
    console.warn('[formatInterviewData] Missing critical IDs in raw interview data:', {
      id: interview.id,
      has_interviewer_id: !!interview.interviewer_id,
      has_candidate_id: !!interview.candidate_id,
      interview_keys: Object.keys(interview)
    });
  }

  // Ensure proper Date parsing - handle ISO strings, timestamps, and Date objects
  let scheduledAt;
  if (interview.scheduled_at instanceof Date) {
    scheduledAt = interview.scheduled_at;
  } else if (typeof interview.scheduled_at === 'string') {
    scheduledAt = new Date(interview.scheduled_at);
  } else if (typeof interview.scheduled_at === 'number') {
    scheduledAt = new Date(interview.scheduled_at);
  } else {
    scheduledAt = new Date();
  }

  // Validate the Date is valid
  if (isNaN(scheduledAt.getTime())) {
    console.warn('[formatInterviewData] Invalid scheduled_at value:', interview.scheduled_at);
    scheduledAt = new Date();
  }

  const jobDetails = interview.application?.job || {};
  const interviewer = interview.interviewer || {};
  
  // Use interviewer relationship data if available, fallback to raw interviewer_id
  const employerId = interviewer.id || interview.interviewer_id || '';
  const employerName = interviewer.full_name || 'Unknown Employer';
  const employerEmail = interviewer.email || '';
  
  return {
    id: interview.id,
    jobId: jobDetails.id || '',
    jobTitle: interview.job_title || jobDetails.title || 'Position Interview',
    employerId,
    employerName,
    employerEmail,
    companyName: interviewer.company_name || jobDetails.company_name || '',
    scheduledTime: scheduledAt,
    type: interview.interview_type || 'video',
    duration: interview.duration_minutes || 30,
    status: interview.status || 'scheduled',
    notes: interview.notes || '',
    meetingLink: interview.meeting_link || '',
    feedback: parseFeedback(interview.feedback),
    hiring_status: interview.hiring_status,
    hiring_notes: interview.hiring_notes,
    considered_for_hire: interview.considered_for_hire,
    decision_date: interview.decision_date,
    offer_amount: interview.offer_amount,
    rejection_reason: interview.rejection_reason,
    createdAt: new Date(interview.created_at),
    phoneNumber: interview.phone_number || '',
    // Preserve raw database fields for messaging support
    candidate_id: interview.candidate_id || '',
    interviewer_id: interview.interviewer_id || ''
  };
};

// Modal state reducer for cleaner state management
const modalReducer = (state, action) => {
  switch (action.type) {
    case 'OPEN_DETAILS':
      return { ...state, showDetailsModal: true, selectedInterview: action.payload };
    case 'OPEN_RESCHEDULE':
      return { ...state, showRescheduleModal: true, selectedInterview: action.payload };
    case 'OPEN_CANCEL':
      return { ...state, showCancelModal: true, selectedInterview: action.payload };
    case 'CLOSE_DETAILS':
      return { ...state, showDetailsModal: false, selectedInterview: null };
    case 'CLOSE_RESCHEDULE':
      return { ...state, showRescheduleModal: false, selectedInterview: null };
    case 'CLOSE_CANCEL':
      return { ...state, showCancelModal: false, selectedInterview: null };
    case 'OPEN_SCHEDULER':
      return { ...state, showScheduler: true };
    case 'CLOSE_SCHEDULER':
      return { ...state, showScheduler: false };
    default:
      return state;
  }
};

const initialModalState = {
  showDetailsModal: false,
  showRescheduleModal: false,
  showCancelModal: false,
  showScheduler: false,
  selectedInterview: null
};

// ========== Interview Transformation Utility ==========
const transformInterviewForDisplay = (interview, currentUser, userProfile) => ({
  id: interview.id,
  status: interview.status,
  candidate_name: interview.employerName || 'Employer',
  candidate_email: '',
  candidate_avatar: null,
  employer_name: interview.employerName || 'Employer',
  employer_email: '',
  job_title: interview.jobTitle,
  job: {
    title: interview.jobTitle,
    company_name: interview.companyName,
    location: 'Remote'
  },
  scheduled_at: interview.scheduledTime?.toISOString(),
  duration: interview.duration,
  type: interview.type,
  meeting_link: interview.meetingLink,
  notes: interview.notes,
  feedback: interview.feedback,
  hiring_status: interview.hiring_status,
  hiring_notes: interview.hiring_notes,
  considered_for_hire: interview.considered_for_hire,
  offer_amount: interview.offer_amount,
  decision_date: interview.decision_date,
  rejection_reason: interview.rejection_reason,
  employer: {
    full_name: interview.employerName,
    company_name: interview.companyName
  },
  applicant: {
    full_name: userProfile?.full_name || currentUser?.user_metadata?.full_name || currentUser?.email,
    email: userProfile?.email || currentUser?.email
  }
});

const InterviewsPage = () => {
  const { user: currentUser, userProfile } = useAuth();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalState, dispatchModal] = useReducer(modalReducer, initialModalState);

  // Memoized fetch function to prevent unnecessary re-renders
  const fetchInterviews = useCallback(async () => {
    if (!currentUser?.id) {
      setLoading(false);
      return;
    }

    try {
      const { interviews: fetchedInterviews, error } = await supabaseService.interviews.getCandidateInterviews(currentUser.id);
      
      if (error) {
        throw new Error(error);
      }

      const formattedInterviews = fetchedInterviews.map(interview => 
        formatInterviewData(interview, currentUser)
      );
      
      setInterviews(formattedInterviews);
      setError(null);
    } catch {
      setError('Failed to load interviews. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  // Fetch interviews from Supabase
  useEffect(() => {
    fetchInterviews();
  }, [fetchInterviews]);

  // Real-time subscription for interview updates
  useEffect(() => {
    if (!currentUser?.id) return;

    // Subscribe to interview changes for this candidate
    const interviewChannel = supabase
      .channel('candidate_interviews')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'interview_schedules',
          filter: `candidate_id=eq.${currentUser.id}`
        },
        (payload) => {
          console.log('Interview change detected:', payload);
          
          // Refetch interviews to get the latest data with joins
          fetchInterviews();
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(interviewChannel);
    };
  }, [currentUser?.id, fetchInterviews]);

  // Memoized filtered interviews to prevent unnecessary recalculations
  const filteredInterviews = useMemo(() => {
    const now = new Date();
    
    switch (filter) {
      case 'upcoming':
        return interviews.filter(interview => {
          const isScheduled = interview.status === 'scheduled';
          const isInFuture = interview.scheduledTime && interview.scheduledTime > now;
          return isScheduled && isInFuture;
        });
      
      case 'completed':
        return interviews.filter(interview => interview.status === 'completed');
      
      case 'cancelled':
        return interviews.filter(interview => interview.status === 'cancelled');
      
      case 'expired':
        return interviews.filter(interview => {
          if (!interview.scheduledTime) return false;
          
          // Interview is expired if its scheduled time is in the past
          // This applies to any interview regardless of status:
          // - 'scheduled' interviews past their time
          // - 'completed' interviews  
          // - 'expired' status interviews
          // - 'not_moving_forward' or other hiring status interviews
          return interview.scheduledTime < now;
        });
      
      default:
        return interviews;
    }
  }, [interviews, filter]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  // Memoized statistics to prevent unnecessary recalculations
  const interviewStats = useMemo(() => {
    const now = new Date();
    const upcoming = interviews.filter(i => 
      i.status === 'scheduled' && i.scheduledTime && i.scheduledTime > now
    ).length;
    const completed = interviews.filter(i => i.status === 'completed').length;
    // Expired: any interview with a scheduled time in the past
    const expired = interviews.filter(i => 
      i.scheduledTime && i.scheduledTime < now
    ).length;
    const total = interviews.length;
    const successRate = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { upcoming, completed, expired, total, successRate };
  }, [interviews]);

  // Optimized handler functions with useCallback to prevent re-renders
  const handleRescheduleInterview = useCallback((interviewId) => {
    const interview = interviews.find(i => i.id === interviewId);
    if (interview) {
      dispatchModal({ type: 'OPEN_RESCHEDULE', payload: interview });
    }
  }, [interviews]);

  const handleViewDetails = useCallback((interviewId) => {
    const interview = interviews.find(i => i.id === interviewId);
    if (interview) {
      dispatchModal({ type: 'OPEN_DETAILS', payload: interview });
    }
  }, [interviews]);

  const handleCancelInterview = useCallback((interviewId) => {
    const interview = interviews.find(i => i.id === interviewId);
    if (interview) {
      dispatchModal({ type: 'OPEN_CANCEL', payload: interview });
    }
  }, [interviews]);

  const handleMessageUser = useCallback((interview) => {
    // Navigate to messaging with the employer/candidate
    if (!interview) return;
    
    try {
      const isVA = currentUser?.role === 'va';
      let recipientId;
      let recipientName;
      
      if (isVA) {
        // VA messaging employer
        // Use employerId from formatted data, or fall back to raw interviewer_id
        recipientId = interview.employerId || interview.interviewer_id;
        recipientName = interview.employerName || 'Employer';
      } else {
        // Employer messaging VA
        // Use candidate_id from formatted data
        recipientId = interview.candidate_id || interview.applicant?.id;
        recipientName = interview.candidate_name || interview.applicant?.full_name || 'Candidate';
      }
      
      if (!recipientId) {
        console.warn('Could not determine recipient ID from interview object:', {
          interviewId: interview.id,
          userRole: currentUser?.role,
          employerId: interview.employerId,
          interviewer_id: interview.interviewer_id,
          candidate_id: interview.candidate_id,
          applicant: interview.applicant
        });
        alert('Unable to start messaging. Recipient information is not available.');
        return;
      }
      
      // Navigate to messaging page with the recipient
      window.location.href = `/messages?recipientId=${recipientId}&recipientName=${encodeURIComponent(recipientName || 'User')}`;
    } catch (error) {
      console.error('Error handling message action:', error);
      alert('An error occurred while trying to start messaging.');
    }
  }, [currentUser?.role]);

  const handleScheduleNew = useCallback(() => {
    dispatchModal({ type: 'OPEN_SCHEDULER' });
  }, []);


  const performReschedule = useCallback(async (interviewId, newDateTime, reason) => {
    const { error } = await supabaseService.interviews.rescheduleInterview(
      interviewId, 
      newDateTime, 
      reason
    );
    
    if (error) {
      throw new Error(error);
    }

    setInterviews(prev => prev.map(interview => 
      interview.id === interviewId 
        ? { ...interview, scheduledTime: new Date(newDateTime), notes: reason || interview.notes }
        : interview
    ));

    dispatchModal({ type: 'CLOSE_RESCHEDULE' });
  }, []);

  const performCancel = useCallback(async (interviewId, reason) => {
    const { error } = await supabaseService.interviews.cancelInterview(interviewId, reason);
    
    if (error) {
      throw new Error(error);
    }

    setInterviews(prev => prev.map(interview => 
      interview.id === interviewId 
        ? { ...interview, status: 'cancelled', notes: reason ? `Cancelled: ${reason}` : 'Interview cancelled' }
        : interview
    ));

    dispatchModal({ type: 'CLOSE_CANCEL' });
  }, []);

  const handleStatusChange = useCallback(async (interviewId, newStatus, interviewData) => {
    const interview = interviews.find(i => i.id === interviewId);
    if (!interview) {
      console.error('[InterviewsPage] Interview not found:', interviewId);
      return;
    }

    try {
      let result;
      
      // Handle different status changes appropriately
      switch (newStatus) {
        case 'completed':
          result = await supabaseService.interviews.updateInterviewStatus(interviewId, newStatus);
          break;
        case 'cancelled':
          result = await supabaseService.interviews.cancelInterview(interviewId, 'Cancelled by candidate');
          break;
        default:
          result = await supabaseService.interviews.updateInterviewStatus(interviewId, newStatus);
      }

      if (result.error) {
        throw new Error(result.error);
      }

      // Update local state optimistically
      setInterviews(prev => prev.map(i => {
        if (i.id !== interviewId) return i;
        if (newStatus === 'rescheduled' && interviewData?.id === interviewId) {
          return {
            ...i,
            status: 'rescheduled',
            scheduledTime: interviewData.scheduledTime || i.scheduledTime,
            duration: interviewData.duration || i.duration,
            type: interviewData.type || i.type,
            meetingLink: interviewData.meetingLink || i.meetingLink,
            notes: interviewData.notes || i.notes,
            ...interviewData
          };
        }
        return { ...i, status: newStatus };
      }));

      // Refresh to ensure we have the latest data
      await fetchInterviews();
      
    } catch (error) {
      console.error('[InterviewsPage] Error updating interview status:', {
        interviewId,
        newStatus,
        error: error.message || error
      });
      alert('Failed to update interview status. Please try again.');
    }
  }, [interviews, fetchInterviews]);

  // Transform interviews data to match InterviewCard expectations
  const transformedInterviews = useMemo(() => 
    filteredInterviews.map(interview => transformInterviewForDisplay(interview, currentUser, userProfile)),
    [filteredInterviews, currentUser, userProfile]
  );

  // Calculate pagination data
  const paginationData = useMemo(() => {
    const totalItems = transformedInterviews.length;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedItems = transformedInterviews.slice(startIndex, endIndex);
    
    return {
      totalItems,
      totalPages,
      startIndex,
      endIndex,
      paginatedItems,
      currentPage
    };
  }, [transformedInterviews, currentPage]);

  const handleInterviewScheduled = useCallback(async (newInterview) => {
    try {
      const interviewData = {
        application_id: newInterview.applicationId || null,
        interviewer_id: newInterview.employerId,
        candidate_id: currentUser.id,
        date: new Date(newInterview.scheduledTime).toISOString().split('T')[0],
        time: new Date(newInterview.scheduledTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }),
        duration: newInterview.duration,
        type: newInterview.type,
        meeting_link: newInterview.meetingLink,
        notes: newInterview.notes,
        jobTitle: newInterview.jobTitle || null
      };

      const { interview, error } = await supabaseService.interviews.createInterview(interviewData);
      
      if (error) {
        throw new Error(error);
      }
      
      const scheduledAt = new Date(interview.scheduled_at);
      const jobTitle = interview.job_title || interview.application?.job?.title || newInterview.jobTitle || 'Position Interview';
      
      const formattedInterview = {
        id: interview.id,
        jobId: interview.application?.job?.id || '',
        jobTitle,
        employerId: newInterview.employerId,
        employerName: newInterview.employerName,
        companyName: '',
        scheduledTime: scheduledAt,
        type: interview.interview_type,
        duration: interview.duration_minutes,
        status: 'scheduled',
        notes: interview.notes || '',
        meetingLink: interview.meeting_link || '',
        createdAt: new Date(interview.created_at || Date.now())
      };
      
      setInterviews(prev => [formattedInterview, ...prev]);
      dispatchModal({ type: 'CLOSE_SCHEDULER' });
    } catch {
      throw new Error('Failed to schedule interview. Please try again.');
    }
  }, [currentUser?.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <VAHeaderNav 
          title="Interviews" 
          subtitle="Manage your interview schedule and opportunities" 
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">
              Error Loading Interviews
            </h3>
            <p className="text-red-700 mb-4">
              {error}
            </p>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <VAHeaderNav 
        title="Interviews" 
        subtitle="Manage your interview schedule and opportunities" 
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Action Header */}
        <div className="mb-8">
          <p className="text-sm text-gray-600">
            Track and manage all your interview opportunities
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar size={24} className="text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Upcoming</p>
                <p className="text-2xl font-bold text-gray-900">{interviewStats.upcoming}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle size={24} className="text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-gray-900">
                  {interviewStats.completed}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <BarChart3 size={24} className="text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{interviewStats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Star size={24} className="text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Success Rate</p>
                <p className="text-2xl font-bold text-gray-900">
                  {interviewStats.successRate}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid with Sidebar */}
        <div className="flex flex-col lg:flex-row lg:space-x-6">
          
          {/* Sidebar - Vertical Tabs for Desktop */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1">
              {/* View Mode Toggle */}
            <div className="flex justify-start mb-4">
              <div className="flex items-center space-x-2 bg-white rounded-lg border border-gray-200 p-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    viewMode === 'list' 
                      ? 'text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  style={viewMode === 'list' ? { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' } : {}}
                >
                  List
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    viewMode === 'grid' 
                      ? 'text-white' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  style={viewMode === 'grid' ? { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' } : {}}
                >
                  Grid
                </button>
              </div>
            </div>
              <div className="space-y-1">
                {[
                  { key: 'all', label: 'All Interviews', count: interviewStats.total },
                  { key: 'upcoming', label: 'Upcoming', count: interviewStats.upcoming },
                  { key: 'completed', label: 'Completed', count: interviewStats.completed },
                  { key: 'expired', label: 'Expired', count: interviewStats.expired },
                  { key: 'cancelled', label: 'Cancelled', count: interviews.filter(i => i.status === 'cancelled').length }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setFilter(tab.key)}
                    className={`w-full text-left px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                      filter === tab.key
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{tab.label}</span>
                      {tab.count > 0 && (
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          filter === tab.key
                            ? 'bg-blue-200 text-blue-800'
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          {tab.count}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile Dropdown */}
          <div className="lg:hidden mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <label htmlFor="interview-filter" className="block text-sm font-medium text-gray-700 mb-2">
                Filter Interviews
              </label>
              <select
                id="interview-filter"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Interviews ({interviewStats.total})</option>
                <option value="upcoming">Upcoming ({interviewStats.upcoming})</option>
                <option value="completed">Completed ({interviewStats.completed})</option>
                <option value="expired">Expired ({interviewStats.expired})</option>
                <option value="cancelled">Cancelled ({interviews.filter(i => i.status === 'cancelled').length})</option>
              </select>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1">
            

            {/* Interviews List */}
            {transformedInterviews.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <div className="text-4xl mb-4">📅</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No interviews yet... Apply to more jobs!
                </h3>
                <p className="text-gray-600 mb-4">
                  {filter === 'all' 
                    ? "You don't have any scheduled interviews yet."
                    : `No ${filter} interviews to display.`
                  }
                </p>
                {/* <Button onClick={handleScheduleNew}>
                  Schedule Your First Interview
                </Button> */}
              </div>
            ) : (
              <>
                <LazyInterviewList
                  interviews={paginationData.paginatedItems}
                  viewMode={viewMode}
                  onReschedule={handleRescheduleInterview}
                  onCancel={handleCancelInterview}
                  onComplete={(id) => handleStatusChange(id, 'completed')}
                  onMessage={handleMessageUser}
                  onStatusChange={handleStatusChange}
                />


                {/* Pagination Controls */}
                {paginationData.totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="text-sm text-gray-600">
                      Showing {paginationData.startIndex + 1} to {Math.min(paginationData.endIndex, paginationData.totalItems)} of {paginationData.totalItems} interviews
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        ← Previous
                      </button>

                      <div className="flex items-center space-x-1">
                        {Array.from({ length: paginationData.totalPages }, (_, i) => i + 1).map(page => (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                              currentPage === page
                                ? 'bg-blue-600 text-white'
                                : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        ))}
                      </div>

                      <button
                        onClick={() => setCurrentPage(prev => Math.min(paginationData.totalPages, prev + 1))}
                        disabled={currentPage === paginationData.totalPages}
                        className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Interview Scheduler Modal */}
      {modalState.showScheduler && (
        <EnhancedInterviewScheduleModal
          isOpen={true}
          onClose={() => dispatchModal({ type: 'CLOSE_SCHEDULER' })}
          onSubmit={handleInterviewScheduled}
          selectedCandidate={{
            id: currentUser?.id,
            name: currentUser?.user_metadata?.full_name || currentUser?.email
          }}
          initialData={{
            jobId: null,
            employerId: null,
            employerName: "Employer",
            job_title: "Select a Job"
          }}
          currentUserId={currentUser?.id}
        />
      )}

      {/* Interview Details Modal */}
      {modalState.showDetailsModal && modalState.selectedInterview && (
        <InterviewDetailsModal
          interview={modalState.selectedInterview}
          onClose={() => dispatchModal({ type: 'CLOSE_DETAILS' })}
          onReschedule={handleRescheduleInterview}
          onCancel={handleCancelInterview}
        />
      )}

      {/* Interview Reschedule Modal */}
      {modalState.showRescheduleModal && modalState.selectedInterview && (
        <InterviewRescheduleModal
          interview={modalState.selectedInterview}
          onClose={() => dispatchModal({ type: 'CLOSE_RESCHEDULE' })}
          onReschedule={performReschedule}
        />
      )}

      {/* Interview Cancel Modal */}
      {modalState.showCancelModal && modalState.selectedInterview && (
        <InterviewCancelModal
          interview={modalState.selectedInterview}
          onClose={() => dispatchModal({ type: 'CLOSE_CANCEL' })}
          onCancel={performCancel}
        />
      )}
    </div>
  );
};

export default InterviewsPage;
